import {useCallback, useState} from 'react'
import axios from 'axios'
import {toast} from 'react-toastify'
import {apiUrl} from '../../api-utils/constantVariables'

export type ExportReportPayload = Record<
  string,
  string | number | boolean | string[] | undefined
> & {
  type: string
}

/**
 * Triggers a server-side Excel export and downloads the resulting file.
 *
 * Callers pass a flat object such as `{month, type}` (plus any extra filters).
 * The backend `/admin/report` endpoint expects `{ type, filterObj }`, so we
 * wrap the remaining keys (month + any extra filters) into `filterObj` and
 * always send `page: 1` so report queries never crash on a missing page.
 */
export const downloadExportFile = async (data: ExportReportPayload) => {
  const adminData = localStorage.getItem('admin')
  if (!adminData) throw new Error('Not authenticated')
  const parsed = JSON.parse(adminData) as {JWToken?: string; token?: string}
  const access = parsed.JWToken || parsed.token || ''
  if (!access) throw new Error('Missing admin token')

  const {type, ...rest} = data || {}

  const payload = {
    type,
    filterObj: {
      page: 1,
      ...rest,
    },
  }

  const response = await axios.post(`${apiUrl}/admin/report`, payload, {
    headers: {'x-access-token': access},
    responseType: 'blob',
  })

  const contentType = String(response.headers['content-type'] || '')
  if (contentType.includes('application/json')) {
    const text = await (response.data as Blob).text()
    let message = 'Export failed'
    try {
      const json = JSON.parse(text)
      message = json.message || json.error || message
    } catch {
      /* use default */
    }
    throw new Error(message)
  }

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${type || 'data'}-report.xlsx`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/** Shared export loading state + error toast for all admin Export buttons. */
export const useExportReport = () => {
  const [exporting, setExporting] = useState(false)

  const runExport = useCallback(async (data: ExportReportPayload) => {
    setExporting(true)
    try {
      await downloadExportFile(data)
      toast.success('Export downloaded successfully')
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Export failed. Please try again.'
      toast.error(message)
    } finally {
      setExporting(false)
    }
  }, [])

  return {runExport, exporting}
}
